import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, QrCode, Share2, Copy, Users, Upload, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Final confirmation step.
 *
 * Layout:
 *  - Header (success icon, code)
 *  - ONE big QR (for the booking, or for the GROUP holder if multi-room).
 *  - Below, if it's a group: compact "Condividi link con i membri" list.
 *  - "What happens next" + navigation buttons.
 */
export default function Step5Confirm({ t, confirmation, navigate, publicPayMethods = [], totalPaid = 0, depositAmount = 0, rateType = "" }) {
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  // Detect single vs group. For groups, the primary code (used for the BIG
  // QR) is the group_code, which the App Guida can resolve to the holder's
  // view of all rooms.
  const isGroup = Array.isArray(confirmation?.bookings) && confirmation.bookings.length > 1;
  const primaryCode = isGroup
    ? (confirmation?.group_code || confirmation?.code)
    : confirmation?.code;
  const subBookings = isGroup ? confirmation.bookings : [];

  const guideBase = process.env.REACT_APP_GUIDE_APP_URL || "https://booking-guide-plus.preview.emergentagent.com";
  const buildGuideUrl = (code) => `${guideBase}/?code=${encodeURIComponent(code)}`;
  const buildQrUrl = (code) => `${API_BASE}/api/public/bookings/${encodeURIComponent(code)}/qr.png`;

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(buildGuideUrl(code));
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const shareLink = async (code, label) => {
    const url = buildGuideUrl(code);
    const text = `Ciao! Per il nostro soggiorno al B&B Emmanuel${label ? ` (${label})` : ""}, completa il tuo check-in qui: ${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Check-in B&B Emmanuel", text, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, "_blank", "noopener");
  };

  // Memo the share list (rooms with their share label)
  const groupShareList = useMemo(() => {
    return subBookings.map((b, i) => ({
      code: b.code,
      label: b.room_name || `Camera ${i + 1}`,
      index: i + 1,
    }));
  }, [subBookings]);

  return (
    <div data-testid="booking-step-5" className="py-6">
      {/* iter238 — Banner evidente "in attesa" quando la prenotazione
          non è auto-confermata (deposit_30 / on_request / gruppo). L'host
          vuole che il cliente sia informato che NON riceverà una seconda
          email finché lui non conferma manualmente dall'admin. */}
      {(() => {
        const status = confirmation?.status || confirmation?.bookings?.[0]?.status || "pending";
        if (status === "confirmed") return null;
        return (
          <div className="max-w-xl mx-auto mb-5 bg-[#FFF4D6] border-2 border-[#E3A857] rounded-xl p-4 text-center" data-testid="pending-confirmation-banner">
            <div className="text-2xl mb-1">⏳</div>
            <h3 className="font-semibold text-[#7A5410] text-base mb-1">In attesa di conferma dell&apos;host</h3>
            <p className="text-xs text-[#7A5410] leading-relaxed">
              La tua richiesta è stata registrata. L&apos;host verificherà la disponibilità e ti manderà <strong>una singola email di conferma</strong> con tutti i dettagli e le istruzioni per il check-in. Non riceverai altre email finché la prenotazione non sarà confermata.
            </p>
          </div>
        );
      })()}
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#E3A857]/20 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-[#AC5D45]" />
        </div>
        <h2 className="font-display text-3xl text-[#1E3A5F] mb-2">{t.booking.request_received_title}</h2>
        <p className="text-[#635F59] mb-2">{t.booking.success_msg}</p>
        <p
          className="font-display text-3xl text-[#AC5D45] font-semibold mb-6 tracking-wider"
          data-testid="confirm-code"
        >
          {primaryCode}
        </p>
      </div>

      {/* BIG QR card */}
      <div
        className="max-w-xl mx-auto mb-5 bg-white border border-[#E8E2D9] rounded-xl p-5 flex flex-col sm:flex-row gap-5 items-center sm:items-stretch"
        data-testid={`qr-card-${primaryCode}`}
      >
        <div className="shrink-0 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-2">
          <img
            src={buildQrUrl(primaryCode)}
            alt={`QR code per ${primaryCode}`}
            className="w-44 h-44"
            loading="lazy"
            data-testid={`qr-img-${primaryCode}`}
          />
        </div>
        <div className="flex-1 flex flex-col text-center sm:text-left">
          <div className="text-[11px] text-[#AC5D45] font-bold uppercase tracking-wider mb-1">
            {isGroup ? `Prenotazione di gruppo · ${subBookings.length} camere` : "Check-in rapido"}
          </div>
          <div className="font-semibold text-[#1E3A5F] mb-1">
            <QrCode className="w-4 h-4 inline mr-1 -mt-0.5" /> Inquadra il QR
          </div>
          <p className="text-xs text-[#635F59] flex-1 mb-3">
            Apri il QR con la fotocamera del telefono per accedere all&apos;App Guida con la composizione già pre-compilata. {isGroup && "Il capogruppo gestisce il check-in di tutte le camere."}
          </p>
          <div className="flex gap-2 justify-center sm:justify-start flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => shareLink(primaryCode, isGroup ? "Prenotazione di gruppo" : "")}
              data-testid={`qr-share-${primaryCode}`}
              className="border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/10 rounded-full h-8 text-xs"
            >
              <Share2 className="w-3 h-3 mr-1" /> Condividi
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyLink(primaryCode)}
              data-testid={`qr-copy-${primaryCode}`}
              className="border-[#E8E2D9] text-[#635F59] hover:bg-[#FDFBF7] rounded-full h-8 text-xs"
            >
              <Copy className="w-3 h-3 mr-1" /> Copia link
            </Button>
          </div>
        </div>
      </div>

      {/* Group: per-room share buttons */}
      {isGroup && (
        <div className="max-w-xl mx-auto mb-6 p-4 bg-[#AC5D45]/5 border border-[#AC5D45]/30 rounded-xl" data-testid="group-share-block">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[#AC5D45]" />
            <h3 className="font-semibold text-[#1E3A5F] text-sm">Condividi con i membri del gruppo</h3>
          </div>
          <p className="text-[11px] text-[#635F59] mb-3">
            Manda ai tuoi compagni di viaggio il link <strong>specifico della loro camera</strong>: troveranno già il proprio nome pronto. Solo il titolare di camera dovrà caricare il documento, gli altri membri inseriranno solo i dati anagrafici.
          </p>
          <div className="space-y-1.5">
            {groupShareList.map((r) => (
              <div
                key={r.code}
                className="flex items-center justify-between gap-2 bg-white px-3 py-2 rounded-lg border border-[#E8E2D9]"
                data-testid={`group-share-row-${r.code}`}
              >
                <span className="flex-1 truncate text-sm text-[#1E3A5F]">
                  <span className="text-[#AC5D45] font-semibold">#{r.index}</span>{" "}
                  {r.label}{" "}
                  <span className="font-mono text-[10px] text-[#9C9890]">· {r.code}</span>
                </span>
                <button
                  type="button"
                  onClick={() => shareLink(r.code, r.label)}
                  className="text-xs text-[#AC5D45] font-semibold hover:underline px-2 py-1"
                  data-testid={`group-share-btn-${r.code}`}
                >
                  <Share2 className="w-3 h-3 inline mr-0.5" /> Condividi
                </button>
                <button
                  type="button"
                  onClick={() => copyLink(r.code)}
                  className="text-xs text-[#635F59] hover:text-[#AC5D45] px-1.5 py-1"
                  title="Copia link"
                  data-testid={`group-copy-btn-${r.code}`}
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto bg-[#FDFBF7] border border-[#E8E2D9] rounded-xl p-5 text-left mb-6">
        <h3 className="font-semibold text-[#1E3A5F] mb-3">{t.booking.what_happens_next}</h3>
        <ol className="text-sm text-[#2D2926] space-y-2 list-decimal pl-5">
          <li>{t.booking.next_step1}</li>
          <li>{t.booking.next_step2}</li>
          <li>{t.booking.next_step3}</li>
        </ol>
      </div>

      {/* Payment instructions — mostra i dati del metodo di pagamento scelto
          dal cliente (IBAN se bonifico, QR/email se PayPal, ecc.) così può
          effettuare il pagamento immediatamente senza dover aspettare la
          seconda email. Segnalato dall'host 01/07/2026: senza questo blocco
          i clienti perdevano l'IBAN se non se lo erano appuntati. */}
      <PaymentInstructionsBlock
        publicPayMethods={publicPayMethods}
        confirmation={confirmation}
        primaryCode={primaryCode}
        totalPaid={totalPaid}
        depositAmount={depositAmount}
        rateType={rateType}
      />

      {/* Payment receipt upload — speeds up the confirmation. If the guest
          has already paid (via QR / bank transfer / etc.) she can attach a
          screenshot or PDF receipt here; the host gets a Telegram ping. */}
      <ReceiptUploadBlock primaryCode={primaryCode} confirmation={confirmation} />

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={() => navigate(`/booking/resume/${primaryCode}`)}
          data-testid="view-resume"
          className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full px-7 h-11"
        >
          {t.booking.view_booking}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/")}
          data-testid="back-home"
          className="rounded-full px-7 h-11"
        >
          Home
        </Button>
      </div>
    </div>
  );
}


/**
 * ReceiptUploadBlock — lets the guest attach a payment receipt right after
 * the booking is confirmed, accelerating the host's "payment verified" status.
 */
function ReceiptUploadBlock({ primaryCode, confirmation }) {
  const [file, setFile] = useState(null);
  const [b64, setB64] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const total = Number(confirmation?.total || 0);
  const guestName = `${confirmation?.guest?.first_name || ""} ${confirmation?.guest?.last_name || ""}`.trim();
  const methodId = confirmation?.payment_method || null;

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Ricevuta troppo grande (max 3 MB). Riducila o fai uno screenshot.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFile(f);
      setB64(String(reader.result || ""));
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!b64) {
      toast.error("Seleziona prima la ricevuta");
      return;
    }
    setSending(true);
    try {
      await api.post(`/bookings/${encodeURIComponent(primaryCode)}/upload-receipt`, {
        booking_code: primaryCode,
        method_id: methodId,
        amount: total || 0,
        purpose: "deposit",
        purpose_detail: "Ricevuta allegata dal cliente dopo prenotazione",
        receipt_image_b64: b64,
        guest_name: guestName,
      });
      setSent(true);
      toast.success("Ricevuta inviata! L'host verificherà a breve.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore nell'invio";
      toast.error(typeof msg === "string" ? msg : "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="max-w-lg mx-auto bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 mb-6 text-center"
        data-testid="receipt-uploaded-ack"
      >
        <FileCheck2 className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
        <h3 className="font-semibold text-emerald-900 mb-1">Ricevuta inviata!</h3>
        <p className="text-sm text-emerald-800">
          L&apos;host riceverà una notifica e verificherà l&apos;accredito a breve. Riceverai un&apos;email di conferma definitiva.
        </p>
      </div>
    );
  }

  return (
    <div
      className="max-w-lg mx-auto bg-[#FFF8EF] border border-[#E3A857]/40 rounded-xl p-5 mb-6"
      data-testid="receipt-upload-block"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#E3A857]/20 flex items-center justify-center shrink-0">
          <Upload className="w-5 h-5 text-[#AC5D45]" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[#1E3A5F] mb-1">Hai già pagato? Allega la ricevuta</h3>
          <p className="text-xs text-[#635F59]">
            Se hai già effettuato il pagamento (QR, bonifico, PayPal…) puoi caricare ora lo screenshot o il PDF della ricevuta.
            Verrai confermato più rapidamente.
          </p>
        </div>
      </div>
      <div className="space-y-3">
        <input
          type="file"
          accept="image/*,.pdf"
          onChange={onPick}
          className="text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-[#AC5D45] file:text-white file:cursor-pointer w-full"
          data-testid="receipt-file-input"
        />
        {file && (
          <div className="text-xs text-[#635F59] bg-white rounded-lg px-3 py-2 border border-[#E8E2D9]">
            📎 {file.name} · {(file.size / 1024).toFixed(0)} KB · Importo: <strong>€{total.toFixed(2)}</strong>
          </div>
        )}
        <Button
          onClick={submit}
          disabled={!b64 || sending}
          className="w-full bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-11 disabled:opacity-50"
          data-testid="receipt-submit"
        >
          {sending ? "Invio…" : "Invia ricevuta all&apos;host"}
        </Button>
      </div>
    </div>
  );
}


/**
 * PaymentInstructionsBlock — mostra i dati del metodo di pagamento scelto
 * dal cliente durante la prenotazione. Ferma il cliente sulla pagina con
 * TUTTI i dati necessari per pagare subito (IBAN, PayPal, QR, ecc.) senza
 * dover aspettare la seconda email di approvazione dell'host.
 */
function PaymentInstructionsBlock({ publicPayMethods, confirmation, primaryCode, totalPaid, depositAmount, rateType }) {
  const methodId = confirmation?.payment_method;
  const method = (publicPayMethods || []).find((p) => p.id === methodId);
  if (!method) return null;

  const isPrepay = rateType === "non_refundable";
  const amountToPay = isPrepay ? (totalPaid || confirmation?.total || 0) : (depositAmount || Number(confirmation?.total || 0) * 0.30);
  const guestSurname = (confirmation?.guest?.last_name || "").toUpperCase();
  const causale = `${guestSurname} ${primaryCode}`.trim();

  const hasDetails = method.instructions || method.account_detail || method.qr_image || method.holder_name;
  if (!hasDetails) return null;

  return (
    <div className="max-w-lg mx-auto bg-white border-2 border-[#AC5D45] rounded-xl p-5 text-left mb-6 shadow-sm" data-testid="payment-instructions-block">
      <div className="flex items-start gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#AC5D45]/10 flex items-center justify-center flex-shrink-0">
          <span className="text-[#AC5D45] text-lg">💳</span>
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg text-[#1E3A5F] leading-tight">Come pagare — {method.label}</h3>
          <p className="text-xs text-[#635F59] mt-0.5">
            {isPrepay
              ? `Paga € ${Number(amountToPay).toFixed(2)} (totale, tariffa non rimborsabile) per confermare subito la prenotazione`
              : `Versa l'acconto del 30% di € ${Number(amountToPay).toFixed(2)} entro 24h dall'approvazione`}
          </p>
        </div>
      </div>

      <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 space-y-2 text-sm text-[#2D2926]">
        {method.holder_name && (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs text-[#635F59] uppercase tracking-wide">Intestato a:</span>
            <strong className="text-[#1E3A5F]">{method.holder_name}</strong>
          </div>
        )}
        {method.holder_tax_id && (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs text-[#635F59] uppercase tracking-wide">CF:</span>
            <code className="font-mono text-xs text-[#1E3A5F] bg-white border border-[#E8E2D9] rounded px-1.5 py-0.5">{method.holder_tax_id}</code>
          </div>
        )}
        {method.account_detail && (
          <div>
            <div className="text-xs text-[#635F59] uppercase tracking-wide mb-1">Coordinate / account:</div>
            <code className="font-mono text-xs text-[#1E3A5F] bg-white border border-[#E8E2D9] rounded px-2 py-1.5 break-all block select-all" data-testid="payment-account-detail">
              {method.account_detail}
            </code>
          </div>
        )}
        <div>
          <div className="text-xs text-[#635F59] uppercase tracking-wide mb-1">Causale (importante!):</div>
          <code className="font-mono text-xs text-[#1E3A5F] bg-white border border-[#AC5D45]/30 rounded px-2 py-1.5 block select-all" data-testid="payment-causale">
            {causale}
          </code>
        </div>
        <div className="pt-1">
          <div className="text-xs text-[#635F59] uppercase tracking-wide mb-1">Importo da versare:</div>
          <div className="font-display text-2xl text-[#AC5D45] font-bold" data-testid="payment-amount">
            € {Number(amountToPay).toFixed(2)}
          </div>
        </div>
        {method.qr_image && (
          <div className="pt-2 border-t border-[#E8E2D9]/60">
            <div className="text-xs text-[#635F59] uppercase tracking-wide mb-1">Scansiona QR:</div>
            <img src={method.qr_image} alt={`QR ${method.label}`} className="w-32 h-32 border border-[#E8E2D9] rounded bg-white object-contain" data-testid="payment-qr" />
          </div>
        )}
        {method.instructions && (
          <div className="pt-2 border-t border-[#E8E2D9]/60 text-xs text-[#635F59] leading-relaxed">
            {method.instructions}
          </div>
        )}
      </div>

      <div className="mt-3 text-[11px] text-[#635F59] italic">
        📩 Ti abbiamo già inviato una mail con questo riepilogo. Dopo aver pagato, allega la ricevuta qui sotto per velocizzare la conferma.
      </div>
    </div>
  );
}
